import { ColumnDef, useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "./table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./pagination";
import { Fragment } from "react/jsx-runtime";
import { range } from "@/src/lib/utils";
import { ReactNode, useEffect } from "react";
import { useSelectionModel } from "@/src/stores/selectionModel";
import clsx from "clsx";

export type DataTablePagination = {
  total: number;
  pagination: {
    page: number;
    pageSize: number;
  };
};

export type DataTableData<T> = {
  data: T[];
} & DataTablePagination;

export type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  onRowClick?: (row: T) => void;
  onColumnHeaderClick?: (column: ColumnDef<T>) => void;
  pagination?: DataTablePagination;
  onPageClick?: (page: number) => void;
  selectActions?: ReactNode;
  isLoading?: boolean;
};

export function DataTable<T>({
  data,
  columns,
  onRowClick,
  onColumnHeaderClick,
  pagination,
  onPageClick,
  selectActions,
  isLoading,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  const selectionModel = useSelectionModel();
  useEffect(() => {
    selectionModel.init();
  }, []);

  return (
    <div className="flex flex-col">
      {selectionModel.hasSelection() && (
        <div className="bg-accent flex justify-between p-4 text-sm">
          <div className="">{selectionModel.selected.length} items selected</div>
          <div className="flex gap-2">
            {selectActions}
            <div className="cursor-pointer" role="button" onClick={() => selectionModel.clear()}>
              |&nbsp;&nbsp;&nbsp;Clear selection
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        {isLoading ? (
          <Loader />
        ) : (
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      onClick={() => onColumnHeaderClick?.(header.column.columnDef)}
                      key={header.id}
                      className={`${header.id === "actions" ? "w-10" : undefined} whitespace-nowrap`}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={onRowClick ? "cursor-pointer" : ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onRowClick?.(row.original);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length}>No data</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
      {!isLoading && <TablePagination onPageClick={onPageClick} pagination={pagination} />}
    </div>
  );
}

export type TablePaginationProps = {
  pagination?: DataTablePagination;
  onPageClick?(page: number): void;
};
export function TablePagination({ onPageClick, pagination }: TablePaginationProps) {
  const pSplit = pagination && !!pagination?.total ? paginationSplit(pagination) : null;
  return (
    pSplit && (
      <div className="w-full flex justify-center bg-white p-4">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => onPageClick?.(pSplit.currentPage - 1)}
                disabled={pSplit.currentPage == 1}
              />
            </PaginationItem>
            {pSplit.segments.map((s, i) => (
              <Fragment key={i}>
                {i > 0 && <PaginationEllipsis />}
                {s.map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink isActive={pSplit.currentPage === page} onClick={() => onPageClick?.(page)}>
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}
              </Fragment>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={() => onPageClick?.(pSplit.currentPage + 1)}
                disabled={pSplit.currentPage == pSplit.lastPage}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    )
  );
}

export function paginationSplit(pagination: DataTablePagination): {
  segments: number[][];
  currentPage: number;
  lastPage: number;
} {
  const currentPage = pagination.pagination.page;
  const totalPages = Math.ceil(pagination.total / pagination.pagination.pageSize);
  const lastPage = totalPages;
  if (totalPages < 7) {
    return {
      segments: [range(1, totalPages + 1)],
      lastPage,
      currentPage,
    };
  }
  if (currentPage <= 4) {
    return {
      segments: [range(1, currentPage + 2), [lastPage - 1, lastPage]],
      lastPage,
      currentPage,
    };
  }
  if (lastPage - currentPage < 4) {
    return {
      segments: [[1, 2], range(currentPage - 1, lastPage + 1)],
      lastPage,
      currentPage,
    };
  }

  return {
    segments: [
      [1, 2],
      [currentPage - 1, currentPage, currentPage + 1],
      [lastPage - 1, lastPage],
    ],
    lastPage,
    currentPage,
  };
}

export const Loader = () => {
  return (
    <div className="w-full h-full flex items-center justify-center gap-2 mx-auto min-h-[400px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={clsx("animate-spin", "w-10 h-10")}
      >
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
};
